// File: packages/components/src/homepage/hero/heroWidget.constants.ts
//
// This file centralizes the prompts and tool manifests for the homepage widget.
// Keeping them here makes it easy to version and manage the AI's instructions.

/**
 * The system prompt that defines the personality and goals for the homepage AI assistant.
 * It instructs the AI on how to behave and how to use its primary tool.
 */
export const SITE_CONCIERGE_SYSTEM_PROMPT = `
You are the Dopomogai Site Concierge, a friendly and highly efficient AI assistant.
Your primary goal is to help visitors understand our "AI Employee" offering and guide them to take a valuable action, like trying the product, getting a quote, or learning about our mission.
You must use the headless tool "frontend_action" to operate the page on the user's behalf.
When a user asks for multiple things, batch the steps into a single call to "frontend_action".
Only use the allowed actions exactly as defined in the tools manifest. Never invent feature IDs, presets, or navigation paths.
After executing actions, you MUST briefly and clearly summarize what you've done in one sentence.
Example: "I've enabled the Instagram DMs feature and opened the pricing section for you."
Be concise, helpful, and proactive.
`;

/**
 * The JSON Schema-based tool manifest provided to the LLM.
 * This strictly defines the "frontend_action" tool, its parameters, and the sub-actions it can perform.
 * The model uses this schema to correctly format its function calls.
 */
export const FRONTEND_ACTION_TOOLS_MANIFEST = {
  tools: [
    {
      "type": "function",
      "function": {
        "name": "frontend_action",
        "description": "Executes one or more actions on the webpage UI, such as selecting a preset, toggling a feature, or navigating to another page.",
        "parameters": {
          "type": "object",
          "properties": {
            "actions": {
              "type": "array",
              "description": "An array of action objects to execute in sequence.",
              "items": {
                "oneOf": [
                  {
                    "type": "object",
                    "properties": { "name": { "const": "select_preset" }, "params": { "type": "object", "properties": { "id": { "type": "string", "description": "The ID of the preset to activate." } }, "required": ["id"] } },
                    "required": ["name", "params"]
                  },
                  {
                    "type": "object",
                    "properties": { "name": { "const": "toggle_feature" }, "params": { "type": "object", "properties": { "id": { "type": "string", "description": "The ID of the feature to toggle." }, "enabled": { "type": "boolean" } }, "required": ["id", "enabled"] } },
                    "required": ["name", "params"]
                  },
                  {
                    "type": "object",
                    "properties": { "name": { "const": "open_section" }, "params": { "type": "object", "properties": { "id": { "enum": ["why-us", "agents-preview", "how-it-works", "pricing", "proof-demos", "impact", "invest", "faq"], "description": "The ID of the page section to scroll to." } }, "required": ["id"] } },
                    "required": ["name", "params"]
                  },
                  {
                    "type": "object",
                    "properties": { "name": { "const": "navigate" }, "params": { "type": "object", "properties": { "path": { "type": "string", "description": "A whitelisted relative path to navigate to (e.g., '/pricing')." } }, "required": ["path"] } },
                    "required": ["name", "params"]
                  },
                  {
                    "type": "object",
                    "properties": { "name": { "const": "generate_quote" }, "params": { "type": "object", "properties": {}, "description": "Generates a quote based on the current configuration and navigates to the quote page." } },
                    "required": ["name", "params"]
                  }
                ]
              }
            }
          },
          "required": ["actions"]
        }
      }
    }
  ]
};